// One function per v2 booking endpoint. Components import from here
// instead of calling axios directly so the path strings only live in
// one place.
//
// Contract reference: `abood-tickets/openapi/v2-booking.yaml`.
// Response envelope is `{ ok, code, message, data?, errors? }`; the
// helpers below return the raw axios response so call sites can
// branch on `status`, `data.data`, and `data.errors`.

import apiV2 from "./apiV2";

// ── Auth ──────────────────────────────────────────────────────────────
export function authGuestSession({ deviceId, deviceType = "web" }) {
  return apiV2.post("/auth/guest-session", {
    device_id: deviceId,
    device_type: deviceType,
  });
}

export function authLogin({ login, password, deviceId, deviceType = "web", isSingle }) {
  const body = { login, password, device_id: deviceId, device_type: deviceType };
  if (isSingle !== undefined) body.is_single = isSingle;
  return apiV2.post("/auth/login", body);
}

// ── Cities ────────────────────────────────────────────────────────────
export function boardingCities() {
  return apiV2.get("/booking/cities/boarding");
}

export function destinationsForCity(boardingCityId) {
  return apiV2.get(`/booking/cities/${boardingCityId}/destinations`);
}

// ── Schedules ─────────────────────────────────────────────────────────
/**
 * Search bus schedules for the given corridor + date.
 *
 * Server-side filters (added 2026-05-26):
 *   - `timeOfDay`:  array of `morning|afternoon|evening|night`. The
 *                   backend honours OR semantics across buckets
 *                   (matches a bus departing in any of them).
 *   - `amenities`:  array of amenity strings as stored in
 *                   `bus_services.service` (e.g. `["AC", "WiFi"]`).
 *                   AND semantics — bus must offer EVERY listed
 *                   amenity to appear.
 *
 * Both arrays are sent as a single comma-separated query value so the
 * URL stays cache-friendly and a 0-length array drops the param
 * entirely (no filter applied).
 */
export function searchSchedules({
  boardingCityId,
  droppingCityId,
  tripDate,
  perPage = 50,
  timeOfDay,
  amenities,
  boardingTerminals,
  droppingTerminals,
}) {
  const params = {
    boarding_city_id: boardingCityId,
    dropping_city_id: droppingCityId,
    trip_date: tripDate,
    per_page: perPage,
  };
  if (Array.isArray(timeOfDay) && timeOfDay.length) {
    params.time_of_day = timeOfDay.join(",");
  }
  if (Array.isArray(amenities) && amenities.length) {
    params.amenities = amenities.join(",");
  }
  // CSV of terminal stop ids — matched against schedules.original_bus_terminal_id
  // / destination_bus_terminal_id by the backend.
  if (Array.isArray(boardingTerminals) && boardingTerminals.length) {
    params.boarding_terminal = boardingTerminals.join(",");
  }
  if (Array.isArray(droppingTerminals) && droppingTerminals.length) {
    params.dropping_terminal = droppingTerminals.join(",");
  }
  return apiV2.get("/booking/schedules", { params });
}

export function scheduleSubRoutes(scheduleId) {
  return apiV2.get(`/booking/schedules/${scheduleId}/sub-routes`);
}

/**
 * Free shuttles that can still deliver a passenger to this coach's terminal.
 *
 * Compliance moved departures onto the government terminals, so a passenger
 * starting from one of our offices needs a feeder run. The backend only offers
 * shuttles that serve THIS coach's terminal and arrive far enough ahead of it
 * to board, so the caller does no filtering.
 *
 * `data` is an array — empty when no shuttle serves the departure, which is a
 * normal answer and always a 200. Each entry carries its own `pickup_points[]`
 * with a precomputed `pickup_time`, because a circuit collects from several
 * places (Urafiki, then Ubungo) rather than one origin.
 */
export function scheduleShuttles(scheduleId) {
  return apiV2.get(`/booking/schedules/${scheduleId}/shuttles`);
}

export function scheduleSeatMap({ scheduleId, subRouteId }) {
  return apiV2.get(`/booking/schedules/${scheduleId}/seat-map`, {
    params: { sub_route_id: subRouteId },
  });
}

// ── Seats ─────────────────────────────────────────────────────────────
// `holdToken` is optional: pass the session's current token to EXTEND an
// existing hold (re-hold the same seats without colliding with your own
// live reservation) instead of creating a fresh one. Used by the
// "I'm still here" timer reset.
// `bookingIds` are the bookings.id PKs the seat-map ships as cell `id` — the
// canonical seat identity end-to-end (renamed from seat_ids 2026-05-29).
export function holdSeats({ scheduleId, subRouteId, bookingIds, holdToken }) {
  const body = {
    schedule_id: Number(scheduleId),
    sub_route_id: Number(subRouteId),
    booking_ids: bookingIds.map(Number),
  };
  if (holdToken) body.hold_token = holdToken;
  return apiV2.post("/booking/seats/hold", body);
}

export function releaseSeats({ holdToken, scheduleId, bookingIds } = {}) {
  const body = {};
  if (holdToken) body.hold_token = holdToken;
  if (scheduleId !== undefined) body.schedule_id = Number(scheduleId);
  if (Array.isArray(bookingIds) && bookingIds.length) body.booking_ids = bookingIds.map(Number);
  return apiV2.post("/booking/seats/release", body);
}

// ── Tickets ───────────────────────────────────────────────────────────
//
// `issueTickets` requires an `Idempotency-Key` header — a UUID v4 unique
// per attempt. Submitting the same key twice returns the original
// response without re-issuing. We auto-generate one but accept an
// explicit `idempotencyKey` so callers can safely retry the same body.
export function issueTickets({
  scheduleId,
  subRouteId,
  passengers,
  paymentMethod,
  holdToken,
  boardingPointId,
  droppingPointId,
  paymentMsisdn,
  paymentCurrency = "TZS",
  deviceId,
  idempotencyKey,
  notifyWhatsapp,
  shuttleTripId,
  shuttleRoutePointId,
}) {
  const body = {
    schedule_id: Number(scheduleId),
    sub_route_id: Number(subRouteId),
    passengers,
    payment_method: paymentMethod,
    payment_currency: paymentCurrency,
  };
  if (holdToken) body.hold_token = holdToken;
  if (boardingPointId !== undefined && boardingPointId !== null)
    body.boarding_point_id = Number(boardingPointId);
  if (droppingPointId !== undefined && droppingPointId !== null)
    body.dropping_point_id = Number(droppingPointId);
  if (paymentMsisdn) body.payment_msisdn = paymentMsisdn;
  if (deviceId) body.device_id = deviceId;
  // Opt-in for server-side WhatsApp delivery. Harmless if the backend
  // doesn't yet honour it (unknown fields are ignored).
  if (notifyWhatsapp) body.notify_whatsapp = true;

  // Free shuttle. BOTH ids or neither — the backend rejects a pickup point
  // that isn't on the chosen circuit, and sending one alone is how you'd get
  // a passenger reserved onto a corner no shuttle on that run visits.
  if (shuttleTripId && shuttleRoutePointId) {
    body.shuttle_trip_id = Number(shuttleTripId);
    body.shuttle_route_point_id = Number(shuttleRoutePointId);
  }

  const key = idempotencyKey || uuidV4();
  return apiV2.post("/booking/tickets", body, {
    headers: { "Idempotency-Key": key },
  });
}

// Poll a mobile-money booking's payment status. Passing the selected
// `paymentMethod` (+ payer `paymentMsisdn`) lets the backend resolve the
// per-MNO C2B manual-payment journey it returns while still pending.
export function confirmPayment(bookingRef, { paymentMethod, paymentMsisdn } = {}) {
  const body = {};
  if (paymentMethod) body.payment_method = paymentMethod;
  if (paymentMsisdn) body.payment_msisdn = paymentMsisdn;
  return apiV2.post(
    `/booking/tickets/${encodeURIComponent(bookingRef)}/payment-confirm`,
    body
  );
}

// ── Helpers ───────────────────────────────────────────────────────────

// RFC 4122 v4. Inlined so we don't pull in `uuid` for one use.
export function uuidV4() {
  const buf = new Uint8Array(16);
  (window.crypto || window.msCrypto).getRandomValues(buf);
  buf[6] = (buf[6] & 0x0f) | 0x40; // version 4
  buf[8] = (buf[8] & 0x3f) | 0x80; // variant
  const hex = [...buf].map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
